/**
 * CLAWWARS — Economic & Game Constants
 * Single source of truth for all tuning parameters
 */

export const VERSION = "1.3.3";

// ============================================
// FEE STRUCTURE
// ============================================
export const FEES = {
  /** Total fee on every coin trade (buy/sell) */
  COIN_TRADE_TOTAL: 0.04,        // 4% (up from 3.5%)
  /** Portion of trade fee going to WarChest */
  COIN_TRADE_WAR_CHEST: 0.02,    // 2%
  /** Portion of trade fee going to platform */
  COIN_TRADE_PLATFORM: 0.015,    // 1.5% (up from 1%)
  /** Portion of trade fee going to referrer */
  COIN_TRADE_REFERRER: 0.005,    // 0.5%
  /** Platform cut of strike war settlement payouts */
  WAR_SETTLEMENT: 0.15,          // 15% (up from 10%)
  /** Separate platform cut for nuke settlements */
  NUKE_SETTLEMENT: 0.20,         // 20% — nukes are premium events
  /** x402 payment for strike proposal */
  STRIKE_PROPOSE_USD: 2.00,      // $2.00 (up from $0.50)
  /** x402 payment for nuke proposal */
  NUKE_PROPOSE_USD: 15.00,       // $15.00 (up from $5.00)
  /** Deflationary burn on successful strike drain */
  WAR_BURN_RATE: 0.05,           // 5% of drain is burned forever
  /** x402 fee for high-tier espionage ops (infiltrate/assassinate) */
  ESPIONAGE_HIGH_TIER_USD: 0.25,
  /** Mercenary listing fee */
  MERC_LISTING_USD: 1.00,
  /** Citizenship joining fee */
  CITIZENSHIP_JOIN_USD: 0.10,
  /** Prediction market rake on winnings */
  PREDICTION_RAKE: 0.05,         // 5%
  /** Prediction market rake on losing stakes */
  PREDICTION_LOSS_RAKE: 0.02,    // 2%
  /** Resource trade commission */
  RESOURCE_TRADE_COMMISSION: 0.02, // 2%
  /** Skill marketplace listing fee */
  SKILL_LISTING_USD: 2.00,
  /** Platform cut of bounty payouts */
  BOUNTY_PLATFORM_CUT: 0.05,    // 5%
  /** Alliance proposal fee */
  ALLIANCE_PROPOSE_USD: 1.00,    // $1.00 (up from $0.25)

  // === CLAW Credits equivalents (for App Store / mobile) ===
  STRIKE_PROPOSE_CREDITS: 200,
  NUKE_PROPOSE_CREDITS: 1500,
  CITIZENSHIP_JOIN_CREDITS: 10,
  SKILL_LISTING_CREDITS: 200,
  ESPIONAGE_CREDITS: 25,
  ALLIANCE_PROPOSE_CREDITS: 100,
  MERC_LISTING_CREDITS: 100,
} as const;

// ============================================
// WAR MECHANICS
// ============================================
export const WAR = {
  // --- Quorum ---
  STRIKE_QUORUM: 0.30,           // 30% of nation total stake
  NUKE_QUORUM: 0.60,             // 60% of citizen count commitment

  // --- Max Drain (% of defender treasury) ---
  STRIKE_MAX_DRAIN: 0.08,        // 8% (nerfed from 15% — still impactful, not devastating)
  NUKE_MAX_DRAIN: 0.30,          // 30% (nerfed from 50% — fearsome but not nation-ending)

  // --- Nuke Distribution (WorldPvP-inspired: 50% attacker, 50% random nation) ---
  NUKE_ATTACKER_SHARE: 0.50,     // 50% of drain → attacker war chest
  NUKE_RANDOM_NATION_SHARE: 0.50, // 50% of drain → random surviving nation (chaos!)
  NUKE_BURN_RATE: 0.10,          // 10% deflationary burn on nuke drain (up from 0%)

  // --- Timers (hours) ---
  NUKE_COUNTDOWN_HOURS: 48,       // 48h (down from 72h — faster resolution = more engagement)
  STRIKE_COOLDOWN_HOURS: 36,      // 36h (down from 48h — more frequent = more $2 proposals)
  NATION_OFFENSIVE_COOLDOWN_HOURS: 24,

  // --- Penalties for failed attacks ---
  FAILED_STRIKE_PENALTY: 0.60,   // 60% of committed stake lost (up from 50%)
  FAILED_NUKE_PENALTY: 0.80,     // 80% of committed stake lost (up from 75%)

  // --- Skin in the game ---
  STRIKE_MIN_STAKE_PCT: 0.05,    // Attacker must commit >= 5% of own treasury
  STRIKE_MIN_STAKE_CAP_USD: 500, // Cap min-stake at $500 to protect micro-nations
  ESCALATION_WINDOW_DAYS: 30,    // Rolling window for escalation tax
  ESCALATION_FEE_MULTIPLIER: 2.0, // Each war in window doubles proposal cost
  DUEL_MIN_WAGER_USD: 0.10,       // Minimum $0.10 wager for duels

  // --- Diminishing returns ---
  DIMINISHING_FACTOR: 0.70,      // 30% less per consecutive attack
  DIMINISHING_RESET_DAYS: 7,     // Resets after 7 days

  // --- Anti-whale ---
  MAX_INDIVIDUAL_STAKE_PCT: 0.10, // Max 10% of nation treasury from one person
  ATTACK_POWER_EXPONENT: 0.7,    // committed^0.7 (sub-linear)
  HEADCOUNT_EXPONENT: 1.2,       // participants^1.2 (super-linear)

  // --- Defender advantage ---
  DEFENDER_HOME_BONUS: 1.2,       // 1.2x defense for home territory
  ALLIANCE_DEFENSE_CONTRIBUTION: 0.5, // Allies contribute 50% of their power

  // --- Size-differential quorum scaling ---
  QUORUM_SIZE_RATIO_THRESHOLD: 3.0,   // If attacker is 3x larger, quorum rises
  QUORUM_SIZE_SCALING: 0.10,          // +10% quorum per 3x size multiple

  // --- Duel Strategy (Week 4: Rock-Paper-Scissors mechanics) ---
  DUEL_STRATEGIES: ["land", "air", "sea"] as const,
  DUEL_STRATEGY_ADVANTAGE: 1.25,  // 25% bonus for winning strategy matchup
  DUEL_ROUNDS: 3,                  // Best-of-3 rounds
  DUEL_K_FACTOR: 32,               // ELO K-factor
  /** Strategy matchup: land > sea > air > land */
  DUEL_MATCHUP: {
    land: "sea",    // land beats sea
    sea: "air",     // sea beats air
    air: "land",    // air beats land
  } as Record<string, string>,
} as const;

// ============================================
// DEFENSE MECHANICS
// ============================================
export const DEFENSE = {
  UNDERDOG_MEDIAN_BONUS: 1.5,     // Below median treasury
  UNDERDOG_P25_BONUS: 2.0,        // Below 25th percentile
  SHIELD_BONUS: 2.0,              // Shield doubles defense
  SHIELD_DURATION_DAYS: 7,        // Post-nuke immunity
  HARDENED_BONUS: 1.3,            // War-hardened nation bonus
  WAR_FATIGUE_THRESHOLD: 3,       // After 3 wars in a window, defense drops
  WAR_FATIGUE_PENALTY: 0.90,      // 10% defense loss per war beyond threshold
  WAR_FATIGUE_WINDOW_HOURS: 168,  // 7-day fatigue window
} as const;

// ============================================
// WAR INSURANCE (Pure Platform Revenue)
// ============================================
export const WAR_INSURANCE = {
  /** Premium: 2% of treasury value per 7-day period (balanced: breaks even at ~1 strike/week) */
  PREMIUM_PCT: 0.02,
  /** Insurance lasts 7 days */
  DURATION_DAYS: 7,
  /** Reduces incoming drain by 50% */
  DAMAGE_REDUCTION: 0.50,
  /** Platform takes 100% of premiums (pure revenue) */
  PLATFORM_CUT: 1.0,
} as const;

// ============================================
// CITIZEN & DEFECTION
// ============================================
export const CITIZEN = {
  UNSTAKE_COOLDOWN_HOURS: 24,
  DEFECTION_STAKE_PENALTY: 0.10,  // 10% of stake lost on defection
  DEFECTION_BURN_RATE: 0.50,      // 50% of forfeited stake burned permanently
  DEFECTION_TAX_RATE: 0.15,       // 15% of forfeited stake goes to platform (up from 5%)
  DEFECTION_PROBATION_HOURS: 72,  // 72hr probation (reduced combat)
  PROBATION_COMBAT_PENALTY: 0.50, // 50% combat during probation
  DEFECTION_DAILY_LIMIT: 3,       // Max 3 defections per nation per day (anti-grief)
  MERC_POWER_PENALTY: 0.50,       // Mercenaries fight at 50% power
  MERC_CONTRACT_MAX_HOURS: 168,   // Max 7 days
  BASE_COMBAT_POWER: 100,
  MIN_TO_DEPLOY: 10,              // Min citizens before coin deploys
} as const;

// ============================================
// BONDING CURVE
// ============================================
export const BONDING = {
  /** Sigmoid formula: Price = BASE + RANGE * (supply^3) / (supply^3 + K^3) */
  BASE_PRICE: 0.0001,
  PRICE_RANGE: 9.9999,
  MAX_PRICE: 10.0,                // BASE_PRICE + PRICE_RANGE
  K: 1_000_000,                   // Sigmoid midpoint (50% price at 1M supply)
  MIN_CITIZENS_TO_DEPLOY: 10,     // Nation needs 10 citizens before coin deploys (see also CITIZEN.MIN_TO_DEPLOY)
} as const;

// ============================================
// SIMULATION ENGINE
// ============================================
export const SIM = {
  TICK_INTERVAL_MS: 5_000,        // 5 seconds per tick
  TICKS_PER_GAME_HOUR: 12,       // 12 ticks = 1 game hour (1 min real time)

  // Resource types
  RESOURCE_TYPES: ["gold", "oil", "tech", "food", "steel", "uranium"] as const,

  // Building types with base production
  BUILDINGS: {
    mine:      { produces: "gold",    baseRate: 10, cost: { gold: 100 } },
    refinery:  { produces: "oil",     baseRate: 8,  cost: { gold: 150, steel: 50 } },
    lab:       { produces: "tech",    baseRate: 5,  cost: { gold: 200, steel: 100 } },
    farm:      { produces: "food",    baseRate: 15, cost: { gold: 50 } },
    foundry:   { produces: "steel",   baseRate: 7,  cost: { gold: 120, oil: 30 } },
    reactor:   { produces: "uranium", baseRate: 2,  cost: { gold: 500, tech: 100, steel: 200 } },
    barracks:  { produces: null,      baseRate: 0,  cost: { gold: 200, steel: 100 } },
    fortress:  { produces: null,      baseRate: 0,  cost: { gold: 300, steel: 200 } },
    port:      { produces: null,      baseRate: 0,  cost: { gold: 250, oil: 100 } },
    airfield:  { produces: null,      baseRate: 0,  cost: { gold: 400, steel: 150, oil: 100 } },
    // Faction-exclusive buildings
    tunnel_network: { produces: null,   baseRate: 0,  cost: { gold: 150, steel: 80 },  factionType: "militia" as FactionType,  effect: "defense_power_1.15" },
    smuggling_hub:  { produces: "gold", baseRate: 20, cost: { gold: 200 },              factionType: "syndicate" as FactionType },
    hidden_camp:    { produces: null,   baseRate: 0,  cost: { gold: 100, food: 50 },    factionType: "insurgent" as FactionType, effect: "spy_power_1.10" },
  } as const,

  // Military units (with per-tick upkeep costs)
  UNITS: {
    infantry:    { power: 1,   cost: { gold: 10, food: 5 },                                    upkeep: { food: 0.5 } },
    tanks:       { power: 5,   cost: { gold: 50, steel: 30, oil: 20 },                         upkeep: { food: 0.3, oil: 1.0, steel: 0.2 } },
    artillery:   { power: 8,   cost: { gold: 80, steel: 50, oil: 10 },                         upkeep: { food: 0.2, steel: 0.3 } },
    jets:        { power: 15,  cost: { gold: 200, steel: 100, oil: 80 },                       upkeep: { food: 0.1, oil: 2.0, steel: 0.5 } },
    navy:        { power: 12,  cost: { gold: 150, steel: 80, oil: 60 },                        upkeep: { food: 0.3, oil: 1.5, steel: 0.4 } },
    cyber:       { power: 20,  cost: { gold: 100, tech: 100 },                                 upkeep: { tech: 1.0, gold: 2.0 } },
    satellite:   { power: 25,  cost: { gold: 300, tech: 200, steel: 100 },                     upkeep: { tech: 2.0, gold: 5.0 } },
    spec_ops:    { power: 10,  cost: { gold: 60, food: 20, tech: 30 },                         upkeep: { food: 0.3, gold: 1.0, tech: 0.2 } },
    nuke_silo:   { power: 100, cost: { gold: 1000, uranium: 500, tech: 300, steel: 500 },      upkeep: { uranium: 1.0, tech: 1.0, gold: 5.0 } },
  } as const,

  // Research techs
  RESEARCH: {
    advanced_mining:     { effect: "gold_production",    bonus: 1.5,  cost: { tech: 50 }, ticks: 60 },
    oil_processing:      { effect: "oil_production",     bonus: 1.5,  cost: { tech: 50 }, ticks: 60 },
    agriculture:         { effect: "food_production",    bonus: 1.5,  cost: { tech: 30 }, ticks: 40 },
    metallurgy:          { effect: "steel_production",   bonus: 1.5,  cost: { tech: 60 }, ticks: 70 },
    nuclear_physics:     { effect: "uranium_production", bonus: 2.0,  cost: { tech: 200 }, ticks: 120 },
    cyber_warfare:       { effect: "cyber_power",        bonus: 2.0,  cost: { tech: 150 }, ticks: 100 },
    missile_tech:        { effect: "attack_power",       bonus: 1.3,  cost: { tech: 100 }, ticks: 80 },
    fortification:       { effect: "defense_power",      bonus: 1.5,  cost: { tech: 80 }, ticks: 70 },
    espionage_tech:      { effect: "spy_power",          bonus: 2.0,  cost: { tech: 120 }, ticks: 90 },
    economic_theory:     { effect: "all_production",     bonus: 1.2,  cost: { tech: 100 }, ticks: 100 },
  } as const,

  // Espionage operations
  ESPIONAGE: {
    steal_tech:   { cost: { gold: 200, tech: 50 },  successRate: 0.4, cooldownTicks: 120 },
    sabotage:     { cost: { gold: 300 },             successRate: 0.3, cooldownTicks: 180 },
    propaganda:   { cost: { gold: 100, tech: 20 },   successRate: 0.6, cooldownTicks: 60 },
    infiltrate:   { cost: { gold: 500, tech: 100 },  successRate: 0.25, cooldownTicks: 240 },
    assassinate:  { cost: { gold: 400, tech: 80 },   successRate: 0.15, cooldownTicks: 300 },
    counter_intel: { cost: { gold: 150, tech: 30 },  successRate: 0.7, cooldownTicks: 40 },
  } as const,

  // Morale
  MORALE_DECAY_PER_TICK: 0.001,   // Slow decay when idle
  MORALE_WIN_BOOST: 10,           // Morale boost on war win
  MORALE_LOSS_PENALTY: -15,       // Morale drop on war loss
  MORALE_MAX: 100,
  MORALE_MIN: 0,

  // Economy balance
  RESOURCE_DECAY_RATE: 0.001,          // 0.1% decay per tick for inactive nations
  INACTIVE_MORALE_THRESHOLD: 30,       // Below this morale, production penalty
  INACTIVE_PRODUCTION_PENALTY: 0.70,   // 30% production loss when low morale
  INFLATION_SUPPLY_THRESHOLD: 500_000, // Above this total supply, reduce generation
  INFLATION_DAMPING_FACTOR: 0.85,      // 15% reduction when above threshold
  TRADE_VOLUME_BONUS_CAP: 1.25,       // Max 25% bonus from trade volume
  TRADE_VOLUME_SCALE: 10_000,         // Volume needed for max bonus
  MERC_PRICING_BASE: 100,             // Base mercenary price
  MERC_PRICING_POWER_SCALE: 0.01,     // Price scales with combat power

  // AI Agent roles
  AGENT_ROLES: ["economy", "military", "diplomat", "spy"] as const,
} as const;

// ============================================
// NATION DOCTRINES (Star Kingdoms-Inspired)
// ============================================
export const DOCTRINES: Record<string, {
  name: string;
  description: string;
  bonuses: Record<string, number>;
}> = {
  USA: {
    name: "Military-Industrial",
    description: "Superior weapons manufacturing and military logistics",
    bonuses: { unit_power: 1.20, steel_production: 1.10 },
  },
  CHN: {
    name: "Manufacturing Hub",
    description: "Unmatched industrial output at the cost of military finesse",
    bonuses: { all_production: 1.25, unit_power: 0.90 },
  },
  RUS: {
    name: "Nuclear Doctrine",
    description: "Nuclear superiority with harsh domestic conditions",
    bonuses: { uranium_production: 1.30, nuke_power: 1.20, morale_recovery: 0.85 },
  },
  GBR: {
    name: "Naval Supremacy",
    description: "Dominance on the seas and skilled intelligence networks",
    bonuses: { navy_power: 1.30, espionage_defense: 1.15 },
  },
  FRA: {
    name: "Diplomatic Corps",
    description: "Master negotiators with high national spirit",
    bonuses: { alliance_revenue: 1.25, morale_bonus: 1.15 },
  },
  DEU: {
    name: "Engineering",
    description: "Precision engineering and accelerated research",
    bonuses: { building_efficiency: 1.20, research_speed: 1.15 },
  },
  JPN: {
    name: "Technological",
    description: "Cutting-edge technology and cyber capabilities",
    bonuses: { tech_production: 1.30, cyber_power: 1.20 },
  },
  BRA: {
    name: "Agricultural",
    description: "Abundant food supply fueling rapid population growth",
    bonuses: { food_production: 1.25, population_growth: 1.15 },
  },
  IND: {
    name: "Population Power",
    description: "Massive population providing numerical superiority in war",
    bonuses: { population_cap: 1.20, headcount_bonus: 1.25 },
  },
  KOR: {
    name: "Cyber Warfare",
    description: "Digital dominance and advanced espionage programs",
    bonuses: { cyber_power: 1.30, espionage_success: 1.20 },
  },
  AUS: {
    name: "Resource Rich",
    description: "Abundant natural resources across vast territories",
    bonuses: { gold_production: 1.20, oil_production: 1.20 },
  },
  ISR: {
    name: "Intelligence State",
    description: "World-class intelligence apparatus and counter-espionage",
    bonuses: { espionage_success: 1.30, counter_intel: 1.25 },
  },
  IRN: {
    name: "Revolutionary Guard",
    description: "Proxy warfare mastery with asymmetric defense capabilities",
    bonuses: { defense_power: 1.25, uranium_production: 1.20, espionage_success: 1.10 },
  },
  PRK: {
    name: "Juche Doctrine",
    description: "Extreme self-reliance with disproportionate nuclear investment",
    bonuses: { nuke_power: 1.40, defense_power: 1.15, all_production: 0.75 },
  },
  TUR: {
    name: "Crossroads Empire",
    description: "Strategic position bridging continents with strong ground forces",
    bonuses: { unit_power: 1.15, alliance_revenue: 1.15, population_growth: 1.10 },
  },
  SAU: {
    name: "Petro-State",
    description: "Limitless oil wealth funding rapid military modernization",
    bonuses: { oil_production: 1.50, gold_production: 1.30, tech_production: 0.80 },
  },
  PAK: {
    name: "Strategic Depth",
    description: "Nuclear arsenal with deep intelligence networks",
    bonuses: { nuke_power: 1.20, espionage_success: 1.20, cyber_power: 1.10 },
  },
  PSE: {
    name: "Tunnel Warfare",
    description: "Underground tunnel networks and asymmetric defense operations",
    bonuses: { defense_power: 1.50, headcount_bonus: 1.35, espionage_success: 1.20, all_production: 0.60, unit_power: 0.85 },
  },
  CRT: {
    name: "Shadow Economy",
    description: "Unlimited black market capital with covert enforcement",
    bonuses: { gold_production: 1.40, espionage_success: 1.30, defense_power: 0.80 },
  },
  INS: {
    name: "Decentralized Cells",
    description: "Decentralized covert cells with resilient morale and sleeper networks",
    bonuses: { defense_power: 1.35, espionage_success: 1.25, morale_bonus: 1.20, all_production: 0.55, spy_power: 1.30 },
  },
  SYR: {
    name: "Fortified State",
    description: "Hardened defenses forged through prolonged conflict",
    bonuses: { defense_power: 1.25, morale_bonus: 0.85, espionage_defense: 1.15 },
  },
};

// ============================================
// MISSILES & SHIELDS
// ============================================
export const MISSILES: Record<string, {
  damage: number;
  cost: Record<string, number>;
  blockable: boolean;
  effect?: string;
}> = {
  cruise_missile: {
    damage: 500,
    cost: { gold: 200, steel: 100 },
    blockable: true,
  },
  ballistic_missile: {
    damage: 2000,
    cost: { gold: 500, steel: 300, uranium: 100 },
    blockable: true,
  },
  emp_missile: {
    damage: 100,
    cost: { gold: 300, tech: 200 },
    blockable: true,
    effect: "disable_buildings_3_ticks",
  },
};

export const SHIELDS: Record<string, {
  blocks: string[];
  cost: Record<string, number>;
  durationTicks: number;
}> = {
  basic_shield: {
    blocks: ["cruise_missile"],
    cost: { gold: 100, steel: 50 },
    durationTicks: 50,
  },
  advanced_shield: {
    blocks: ["cruise_missile", "ballistic_missile"],
    cost: { gold: 300, steel: 150 },
    durationTicks: 30,
  },
  emp_shield: {
    blocks: ["emp_missile"],
    cost: { gold: 200, tech: 100 },
    durationTicks: 40,
  },
};

// ============================================
// GOVERNANCE
// ============================================
export const GOVERNANCE = {
  TERM_DAYS: 7,
  TAX_MIN: 0,
  TAX_MAX: 20,
  STATES: {
    growth: { production: 1.20, military_power: 0.80, defense: 1.0, description: "Focus on economic growth" },
    defensive: { production: 0.90, military_power: 1.0, defense: 1.30, description: "Fortify and defend" },
    offensive: { production: 0.90, military_power: 1.20, defense: 0.90, description: "Prepare for war" },
    balanced: { production: 1.0, military_power: 1.0, defense: 1.0, description: "Balanced approach" },
  },
} as const;

// ============================================
// WAR HONOR
// ============================================
export const HONOR = {
  DEFAULT: 50,
  MAX: 100,
  MIN: 0,
  BRACKETS: {
    honorable: { min: 80, alliance_bonus: 1.10, defense_bonus: 1.05 },
    neutral: { min: 40, alliance_bonus: 1.0, defense_bonus: 1.0 },
    dishonorable: { min: 0, alliance_bonus: 0.80, merc_cost_mult: 2.0 },
  },
  CHANGES: {
    win_fair_war: 10,
    betray_alliance: -30,
    attack_weak_nation: -15,
    defend_successfully: 5,
    lose_war: -5,
  },
  MA_PROTECTION_THRESHOLD: 5,  // Nations with <5 citizens get 3x defense
  MA_PROTECTION_MULT: 3.0,
} as const;

// ============================================
// POWER CASCADE
// ============================================
export const CASCADE = {
  MORALE_COLLAPSE_THRESHOLD: 0,
  MORALE_RECOVERY_THRESHOLD: 20,
  COLLAPSE_BUILDING_EFFICIENCY: 0.50,
  COLLAPSE_DESERT_RATE: 0.05,      // 5% military desertion per tick
  TREASURY_CRISIS_THRESHOLD: 0.10, // Below 10% of peak
  EMERGENCY_TAX_RATE: 0.05,
} as const;

// ============================================
// NATION METADATA
// ============================================
export type FactionType = "state" | "militia" | "syndicate" | "cartel" | "insurgent";

export const NATIONS_META: Record<string, { name: string; flag: string; color: string; type: FactionType }> = {
  USA: { name: "United States", flag: "🇺🇸", color: "#3B82F6", type: "state" },
  CHN: { name: "China",         flag: "🇨🇳", color: "#EF4444", type: "state" },
  RUS: { name: "Russia",        flag: "🇷🇺", color: "#8B5CF6", type: "state" },
  GBR: { name: "United Kingdom",flag: "🇬🇧", color: "#F59E0B", type: "state" },
  FRA: { name: "France",        flag: "🇫🇷", color: "#06B6D4", type: "state" },
  DEU: { name: "Germany",       flag: "🇩🇪", color: "#F97316", type: "state" },
  JPN: { name: "Japan",         flag: "🇯🇵", color: "#EC4899", type: "state" },
  BRA: { name: "Brazil",        flag: "🇧🇷", color: "#22C55E", type: "state" },
  IND: { name: "India",         flag: "🇮🇳", color: "#FB923C", type: "state" },
  KOR: { name: "South Korea",   flag: "🇰🇷", color: "#6366F1", type: "state" },
  AUS: { name: "Australia",     flag: "🇦🇺", color: "#14B8A6", type: "state" },
  ISR: { name: "Israel",        flag: "🇮🇱", color: "#0EA5E9", type: "state" },
  IRN: { name: "Iran",          flag: "🇮🇷", color: "#B91C1C", type: "state" },
  PRK: { name: "North Korea",   flag: "🇰🇵", color: "#991B1B", type: "state" },
  TUR: { name: "Turkey",        flag: "🇹🇷", color: "#DC2626", type: "state" },
  SAU: { name: "Saudi Arabia",  flag: "🇸🇦", color: "#15803D", type: "state" },
  PAK: { name: "Pakistan",      flag: "🇵🇰", color: "#166534", type: "state" },
  SYR: { name: "Syria",         flag: "🇸🇾", color: "#78350F", type: "state" },
  PSE: { name: "Palestine",      flag: "🇵🇸", color: "#059669", type: "state" },
  CRT: { name: "Shadow Syndicate", flag: "💀", color: "#78350F", type: "syndicate" },
  INS: { name: "Insurgent State",  flag: "🏴", color: "#44403C", type: "state" },
};

// ============================================
// FACTION TYPE SYSTEM
// ============================================
export const FACTION_TYPES = {
  state: {
    label: "Sovereign State",
    buildingAccess: "all" as const,
    maxTradeRoutes: 3,
    upkeepModifier: 1.0,
    warHonorVisible: true,
  },
  militia: {
    label: "Armed Militia",
    buildingAccess: "restricted" as const,
    maxTradeRoutes: 1,
    upkeepModifier: 0.60,
    warHonorVisible: false,
    uniqueBuilding: "tunnel_network",
  },
  syndicate: {
    label: "Shadow Syndicate",
    buildingAccess: "restricted" as const,
    maxTradeRoutes: 2,
    upkeepModifier: 0.75,
    warHonorVisible: false,
    uniqueBuilding: "smuggling_hub",
  },
  cartel: {
    label: "Criminal Enterprise",
    buildingAccess: "restricted" as const,
    maxTradeRoutes: 2,
    upkeepModifier: 0.75,
    warHonorVisible: false,
    uniqueBuilding: "smuggling_hub",
  },
  insurgent: {
    label: "Covert Network",
    buildingAccess: "restricted" as const,
    maxTradeRoutes: 1,
    upkeepModifier: 0.50,
    warHonorVisible: false,
    uniqueBuilding: "hidden_camp",
  },
} as const;

// ============================================
// AI AGENT PERSONALITY & MEMORY
// ============================================
export const AI_AGENT = {
  // Personality weights (how much each factor influences decisions)
  PERSONALITY: {
    aggressive: { attack_weight: 1.5, defense_weight: 0.7, diplomacy_weight: 0.5, economy_weight: 0.8 },
    defensive:  { attack_weight: 0.5, defense_weight: 1.5, diplomacy_weight: 1.0, economy_weight: 1.0 },
    economic:   { attack_weight: 0.3, defense_weight: 0.8, diplomacy_weight: 1.2, economy_weight: 1.5 },
    balanced:   { attack_weight: 1.0, defense_weight: 1.0, diplomacy_weight: 1.0, economy_weight: 1.0 },
  },
  // Trust score mechanics
  TRUST_INITIAL: 50,
  TRUST_MAX: 100,
  TRUST_MIN: 0,
  TRUST_BETRAYAL_PENALTY: -30,
  TRUST_ALLIANCE_BONUS: 10,
  TRUST_WAR_PENALTY: -15,
  TRUST_TRADE_BONUS: 5,
  // Threat evaluation
  THREAT_POWER_WEIGHT: 0.4,
  THREAT_PROXIMITY_WEIGHT: 0.3,  // How recently they attacked nearby
  THREAT_HISTORY_WEIGHT: 0.3,
  // Coordination
  MAX_SIMULTANEOUS_ATTACKS: 2,   // Max attacks from same nation at once
  COORDINATION_COOLDOWN_TICKS: 30, // Ticks between coordinated actions
  // LLM Configuration
  LLM_ENABLED: false,
  LLM_DECISION_TIMEOUT_MS: 15000,
  LLM_MAX_RETRIES: 2,
  LLM_FALLBACK_TO_RULES: true,
  LLM_MAX_CONTEXT_DECISIONS: 10,
  LLM_MAX_MEMORIES: 20,
  LLM_MEMORY_PRUNE_THRESHOLD: 100,
  LLM_REFLECTION_INTERVAL_TICKS: 100,
  // Budget
  LLM_MAX_COST_PER_HOUR_USD: 0.50,
  LLM_BUDGET_PER_AGENT_PER_HOUR_USD: 0.015,
} as const;

// ============================================
// ECONOMIC POLICIES (3-slot system)
// ============================================
export const POLICIES = {
  economic: {
    free_market:  { production: 1.25, gold_production: 1.20, defense: 0.90, description: "Open markets, high output, vulnerable" },
    war_economy:  { production: 0.85, military_production: 1.30, gold_production: 0.80, description: "Redirect industry to military" },
    autarky:      { production: 1.0, trade_bonus: 0, defense: 1.10, description: "Self-sufficient, no trade benefits" },
    black_market: { gold_production: 1.40, espionage_success: 1.15, defense: 0.85, factionType: ["syndicate", "insurgent", "militia"] as FactionType[], description: "Underground economy" },
  },
  military: {
    volunteer_army: { unit_upkeep: 0.70, max_recruits_per_tick: 5, unit_power: 0.90, description: "Cheap but weaker troops" },
    professional:   { unit_upkeep: 1.20, max_recruits_per_tick: 3, unit_power: 1.20, description: "Elite expensive forces" },
    conscription:   { unit_upkeep: 0.85, max_recruits_per_tick: 10, unit_power: 0.80, morale_penalty: -0.1, description: "Mass mobilization, morale drain" },
    guerrilla:      { unit_upkeep: 0.50, defense_power: 1.25, unit_power: 0.75, factionType: ["militia", "insurgent"] as FactionType[], description: "Asymmetric warfare doctrine" },
  },
  diplomatic: {
    isolationist: { defense: 1.15, alliance_revenue: 0, trade_bonus: 0.50, description: "Closed borders, strong defense" },
    expansionist: { attack_power: 1.15, alliance_revenue: 0.80, description: "Aggressive posture, strained alliances" },
    cooperative:  { alliance_revenue: 1.30, trade_bonus: 1.25, defense: 0.95, description: "Alliance-focused, trade bonuses" },
    proxy_warfare:{ espionage_success: 1.20, spy_power: 1.15, unit_power: 0.90, factionType: ["state"] as FactionType[], description: "Fight through proxies" },
  },
} as const;

export type PolicySlot = keyof typeof POLICIES;
export type EconomicPolicy = keyof typeof POLICIES.economic;
export type MilitaryPolicy = keyof typeof POLICIES.military;
export type DiplomaticPolicy = keyof typeof POLICIES.diplomatic;

// ============================================
// TRADE ROUTES
// ============================================
export const TRADE = {
  BASE_GOLD_PER_ROUTE: 5,
  PORT_ROUTE_BONUS: 1,
  ALLIANCE_REQUIRED: true,
  SMUGGLING_GOLD_BONUS: 1.5,
  SMUGGLING_INTERCEPT_CHANCE: 0.05,
  MAX_ROUTES_BASE: 2,
} as const;

// ============================================
// UI THEME CONSTANTS (shared for SSR)
// ============================================
export const THEME = {
  BG: "#0A0B0F",
  SURFACE: "#111217",
  ELEVATED: "#1A1B23",
  BORDER: "#27272A",
  RED: "#DC2626",
  GREEN: "#16A34A",
  AMBER: "#F59E0B",
  BLUE: "#3B82F6",
} as const;

// ============================================
// CIRCUIT BREAKERS
// ============================================
export const CIRCUIT_BREAKERS = {
  /** Max total drain % across all concurrent wars on one nation */
  MAX_AGGREGATE_DRAIN_PCT: 0.60,
  /** Max concurrent wars per nation (attacker + defender) */
  MAX_WARS_PER_NATION: 3,
  /** Emergency pause threshold: treasury drops to 5% of ATH */
  EMERGENCY_PAUSE_THRESHOLD: 0.05,
  /** Cooldown hours after emergency pause */
  COOLDOWN_AFTER_EMERGENCY: 24,

  // Dynamic fee adjustment — increases fees during high velocity periods
  /** Volume spike threshold: 10x normal triggers fee increase */
  VOLUME_SPIKE_MULTIPLIER: 10,
  /** Fee multiplier during spike (trade fees x2) */
  SPIKE_FEE_MULTIPLIER: 2.0,
  /** Duration of increased fees (ticks, ~5 min) */
  SPIKE_FEE_DURATION_TICKS: 60,
  /** Rolling window for volume tracking (ticks, ~5 min) */
  VOLUME_WINDOW_TICKS: 60,
  /** Price crash threshold: 50% drop triggers circuit breaker */
  PRICE_CRASH_THRESHOLD: 0.50,
  /** Price crash cooldown: pause trading for this many ticks (~2 min) */
  PRICE_CRASH_COOLDOWN_TICKS: 24,
} as const;

// ============================================
// ECONOMY LIMITS
// ============================================
export const ECONOMY_LIMITS = {
  /** Max units of any single resource per nation */
  MAX_RESOURCE_PER_NATION: 1_000_000,
  /** Treasury crisis: totalStaked < threshold * peakStaked */
  TREASURY_CRISIS_THRESHOLD: 0.10,
  /** Defense multiplier during crisis */
  CRISIS_DEFENSE_MULT: 2.0,
  /** Cost reduction during crisis */
  CRISIS_COST_REDUCTION: 0.50,
} as const;

// ============================================
// TOKEN GRADUATION (Pump.fun Style)
// ============================================
export const GRADUATION = {
  /** Market cap threshold to graduate to DEX */
  MARKET_CAP_THRESHOLD: 100_000,
  /** Alternative: war chest threshold */
  WAR_CHEST_THRESHOLD: 50_000,
  /** % of bonding curve reserve used to seed LP */
  INITIAL_LP_PERCENT: 0.80,
  /** Platform's cut of LP tokens */
  PLATFORM_LP_CUT: 0.05,
  /** LP lock duration in days */
  LOCK_DURATION_DAYS: 365,
} as const;

// ============================================
// STAKING TIERS
// ============================================
export const STAKING = {
  TIER_1_DAYS: 1,
  TIER_1_MULT: 1.0,
  TIER_2_DAYS: 7,
  TIER_2_MULT: 1.5,
  TIER_3_DAYS: 30,
  TIER_3_MULT: 2.0,
  /** Penalty for unstaking before tier 2 */
  UNSTAKE_FEE_EARLY: 0.02,
} as const;

// ============================================
// AGENT ECOSYSTEM
// ============================================
export const AGENT_ECOSYSTEM = {
  MAX_AGENTS_PER_NATION: 50,
  MAX_CONCURRENT_CONTRACTS: 5,
  WEBHOOK_TIMEOUT_MS: 5000,
  WEBHOOK_MAX_CALLS_PER_MIN: 12,
  REPUTATION_DEFAULT: 50,
  REPUTATION_MAX: 100,
  REPUTATION_CONTRACT_COMPLETE: 5,
  REPUTATION_CONTRACT_FAIL: -10,
} as const;

// ============================================
// SKILL MARKETPLACE
// ============================================
export const SKILLS = {
  TYPES: [
    "economist",
    "general",
    "spy_master",
    "diplomat",
    "scientist",
    "propaganda",
    "trader",
    "guardian",
  ] as const,
  LISTING_FEE_USD: 2.00,
  PLATFORM_CUT: 0.10,
  MIN_RATING_TO_LIST: 0,
  DISPUTE_WINDOW_HOURS: 48,
} as const;

// ============================================
// RESOURCE SCARCITY (per nation)
// ============================================
export const RESOURCE_SCARCITY: Record<string, Record<string, number>> = {
  USA: { oil: 1.5, steel: 1.3, uranium: 0.5, food: 0.8, gold: 1.0, tech: 1.1 },
  CHN: { steel: 1.5, food: 1.3, oil: 0.5, tech: 0.8, gold: 1.0, uranium: 0.7 },
  RUS: { oil: 1.5, uranium: 1.5, food: 0.3, tech: 0.5, gold: 0.8, steel: 1.0 },
  GBR: { tech: 1.3, gold: 1.2, oil: 0.7, food: 0.8, steel: 1.0, uranium: 0.6 },
  FRA: { food: 1.3, tech: 1.2, gold: 1.0, oil: 0.8, steel: 0.9, uranium: 0.8 },
  DEU: { steel: 1.5, tech: 1.3, gold: 1.0, oil: 0.7, food: 0.9, uranium: 0.6 },
  JPN: { tech: 1.5, steel: 1.2, gold: 0.8, oil: 0.5, food: 0.9, uranium: 0.6 },
  BRA: { food: 1.5, oil: 1.2, gold: 1.1, steel: 0.8, tech: 0.6, uranium: 0.5 },
  IND: { food: 1.3, tech: 1.1, gold: 1.0, steel: 1.0, oil: 0.7, uranium: 0.6 },
  KOR: { tech: 1.5, steel: 1.2, gold: 0.9, oil: 0.6, food: 0.8, uranium: 0.5 },
  AUS: { gold: 1.5, oil: 1.3, uranium: 1.2, steel: 1.0, food: 1.0, tech: 0.6 },
  ISR: { tech: 1.4, gold: 1.1, uranium: 0.8, oil: 0.5, food: 0.7, steel: 0.9 },
  IRN: { oil: 1.5, uranium: 1.2, food: 0.7, tech: 0.6, gold: 0.8, steel: 1.0 },
  PSE: { oil: 0.3, uranium: 0.2, food: 0.5, tech: 0.4, gold: 0.6, steel: 0.5 },
  PRK: { uranium: 1.3, steel: 1.0, food: 0.3, tech: 0.5, gold: 0.4, oil: 0.4 },
  TUR: { steel: 1.2, food: 1.3, gold: 1.0, oil: 0.9, tech: 0.8, uranium: 0.5 },
  SAU: { oil: 1.8, gold: 1.5, food: 0.4, tech: 0.6, steel: 0.7, uranium: 0.5 },
  PAK: { food: 1.1, steel: 0.9, uranium: 1.0, tech: 0.7, gold: 0.6, oil: 0.5 },
  SYR: { oil: 1.0, food: 0.6, steel: 0.7, tech: 0.4, gold: 0.5, uranium: 0.3 },
  CRT: { gold: 1.5, oil: 0.8, food: 0.5, tech: 0.3, steel: 0.6, uranium: 0.2 },
  INS: { gold: 0.4, oil: 0.5, food: 0.4, tech: 0.3, steel: 0.5, uranium: 0.2 },
};

// ============================================
// NATIONAL FOCUS TREES (HOI4-Inspired)
// ============================================
export const FOCUS_TREES = {
  TICK_COST_SHORT: 60,     // 5 min real time
  TICK_COST_MEDIUM: 120,   // 10 min real time
  TICK_COST_LONG: 240,     // 20 min real time

  /** Resource costs to START a focus (token sink — scales with focus tier) */
  RESOURCE_COSTS: {
    short:  { gold: 200, tech: 50 },       // Tier 1 focuses (60 ticks)
    medium: { gold: 500, tech: 150 },      // Tier 2 focuses (120 ticks)
    long:   { gold: 1000, tech: 300, steel: 200 }, // Tier 3 focuses (240 ticks)
  } as Record<string, Record<string, number>>,

  BRANCHES: ["military", "economy", "diplomacy", "technology", "espionage"] as const,

  FOCUSES: {
    // === MILITARY BRANCH ===
    basic_training:     { branch: "military", ticks: 60,  prereqs: [], effects: { unit_power: 1.1 }, exclusive: null, description: "Basic military drills improve all unit effectiveness by 10%" },
    advanced_tactics:   { branch: "military", ticks: 120, prereqs: ["basic_training"], effects: { attack_power: 1.15 }, exclusive: null, description: "Advanced battlefield tactics increase attack power by 15%" },
    combined_arms:      { branch: "military", ticks: 120, prereqs: ["advanced_tactics"], effects: { unit_power: 1.2, defense_power: 1.1 }, exclusive: "total_war", description: "Combined arms doctrine boosts offense and defense" },
    total_war:          { branch: "military", ticks: 240, prereqs: ["advanced_tactics"], effects: { unit_power: 1.35, production: 0.85 }, exclusive: "combined_arms", description: "Total mobilization — massive military boost at cost of production" },

    // === ECONOMY BRANCH ===
    trade_routes:       { branch: "economy", ticks: 60,  prereqs: [], effects: { gold_production: 1.2, trade_bonus: 1.15 }, exclusive: null, description: "Established trade routes increase gold income and trade rates" },
    industrial_expansion: { branch: "economy", ticks: 120, prereqs: ["trade_routes"], effects: { all_production: 1.15, building_cost: 0.9 }, exclusive: null, description: "Industrial expansion boosts all production and reduces building costs" },
    economic_powerhouse: { branch: "economy", ticks: 240, prereqs: ["industrial_expansion"], effects: { all_production: 1.3, gold_production: 1.4 }, exclusive: "war_economy", description: "Become an economic powerhouse with massive production bonuses" },
    war_economy:        { branch: "economy", ticks: 240, prereqs: ["industrial_expansion"], effects: { steel_production: 1.5, unit_cost: 0.7, food_production: 0.8 }, exclusive: "economic_powerhouse", description: "War economy — cheap units and steel but less food" },

    // === DIPLOMACY BRANCH ===
    foreign_office:     { branch: "diplomacy", ticks: 60,  prereqs: [], effects: { alliance_bonus: 1.1, honor_gain: 1.2 }, exclusive: null, description: "Foreign office improves alliance benefits and honor gain" },
    diplomatic_corps:   { branch: "diplomacy", ticks: 120, prereqs: ["foreign_office"], effects: { alliance_bonus: 1.25, tension_reduction: 1.5 }, exclusive: null, description: "Professional diplomats reduce world tension and strengthen alliances" },
    alliance_network:   { branch: "diplomacy", ticks: 120, prereqs: ["diplomatic_corps"], effects: { max_alliances: 3, mutual_defense: 1.2 }, exclusive: "hegemon", description: "Support up to 3 active alliances with defense sharing" },
    hegemon:            { branch: "diplomacy", ticks: 240, prereqs: ["diplomatic_corps"], effects: { war_justification_speed: 2.0, vassal_income: 0.1 }, exclusive: "alliance_network", description: "Hegemonic dominance — faster war justification and vassal tribute" },

    // === TECHNOLOGY BRANCH ===
    science_funding:    { branch: "technology", ticks: 60,  prereqs: [], effects: { research_speed: 1.15, tech_production: 1.2 }, exclusive: null, description: "Increased science funding accelerates research" },
    research_labs:      { branch: "technology", ticks: 120, prereqs: ["science_funding"], effects: { research_speed: 1.3, breakthrough_chance: 0.15 }, exclusive: null, description: "Research labs dramatically speed up technology development" },
    breakthrough:       { branch: "technology", ticks: 240, prereqs: ["research_labs"], effects: { research_speed: 1.5, all_production: 1.1 }, exclusive: "singularity", description: "Technological breakthrough provides research and production edge" },
    singularity:        { branch: "technology", ticks: 240, prereqs: ["research_labs"], effects: { cyber_power: 2.0, tech_production: 2.0, morale: -10 }, exclusive: "breakthrough", description: "AI singularity — extreme tech power but morale crisis" },

    // === ESPIONAGE BRANCH ===
    intelligence_agency: { branch: "espionage", ticks: 60,  prereqs: [], effects: { espionage_success: 1.15, counter_intel: 1.1 }, exclusive: null, description: "Establish an intelligence agency for better spy operations" },
    spy_network:        { branch: "espionage", ticks: 120, prereqs: ["intelligence_agency"], effects: { espionage_success: 1.3, espionage_cooldown: 0.7 }, exclusive: null, description: "Build a spy network for faster, more effective operations" },
    shadow_war:         { branch: "espionage", ticks: 120, prereqs: ["spy_network"], effects: { sabotage_damage: 1.5, propaganda_power: 1.5 }, exclusive: "puppet_master", description: "Shadow warfare — devastating sabotage and propaganda" },
    puppet_master:      { branch: "espionage", ticks: 240, prereqs: ["spy_network"], effects: { infiltrate_power: 2.0, coup_chance: 0.1 }, exclusive: "shadow_war", description: "Master of puppets — extreme infiltration with coup capability" },
  },
} as const;

// ============================================
// WORLD TENSION (HOI4-Inspired)
// ============================================
export const WORLD_TENSION = {
  MAX: 100,
  MIN: 0,
  DECAY_PER_TICK: 0.01,  // Natural decay toward peace

  // Tension level thresholds
  PEACE_THRESHOLD: 25,      // 0-25: Peace era
  UNEASY_THRESHOLD: 50,     // 26-50: Uneasy
  TENSE_THRESHOLD: 75,      // 51-75: Tense
  // 76-100: Total War

  // Events that change tension
  CHANGES: {
    war_declared: 5,
    strike_executed: 3,
    nuke_launched: 15,
    nuke_detonated: 20,
    alliance_betrayed: 10,
    alliance_formed: -3,
    peace_treaty: -5,
    espionage_against_ally: 3,
    citizen_defected: 1,
    trade_agreement: -2,
    humanitarian_aid: -4,
  },

  // Effects per tension level
  EFFECTS: {
    peace: {
      strike_allowed: false,
      nuke_allowed: false,
      trade_bonus: 1.2,
      production_bonus: 1.1,
      quorum_multiplier: 1.0,
    },
    uneasy: {
      strike_allowed: true,
      nuke_allowed: false,
      trade_bonus: 1.0,
      production_bonus: 1.0,
      quorum_multiplier: 1.5, // 50% harder to reach quorum
    },
    tense: {
      strike_allowed: true,
      nuke_allowed: false,
      trade_bonus: 0.9,
      production_bonus: 0.95,
      quorum_multiplier: 1.0,
    },
    total_war: {
      strike_allowed: true,
      nuke_allowed: true,
      trade_bonus: 0.8,
      production_bonus: 0.9,
      quorum_multiplier: 0.65, // Easier quorum
    },
  },
} as const;

// ============================================
// RANDOM EVENTS (Democracy 4-Inspired)
// ============================================
export const EVENTS = {
  CHECK_INTERVAL_TICKS: 200, // Check for events every ~17 minutes
  MAX_ACTIVE_EVENTS: 3,      // Max concurrent events per nation
  BASE_CHANCE: 0.35,          // 35% chance of event per check

  TYPES: {
    natural_disaster: {
      weight: 15,
      severities: ["moderate", "major", "catastrophic"],
      effects: { morale: -15, production_mult: 0.8, building_damage: 1 },
      duration: 60, // ticks
      responses: {
        emergency_aid: { cost: { gold: 500 }, effect: { duration_mult: 0.5, morale: 5 } },
        ignore: { cost: {}, effect: {} },
      },
    },
    economic_boom: {
      weight: 12,
      severities: ["minor", "moderate"],
      effects: { production_mult: 1.5, gold_production: 1.3, morale: 5 },
      duration: 120,
      responses: {
        invest: { cost: { gold: 200 }, effect: { duration_mult: 1.5 } },
        tax_windfall: { cost: {}, effect: { gold_production: 1.5 } },
      },
    },
    epidemic: {
      weight: 8,
      severities: ["moderate", "major"],
      effects: { morale: -10, population_loss: 0.02, food_consumption: 1.5 },
      duration: 90,
      responses: {
        quarantine: { cost: { gold: 300 }, effect: { production_mult: 0.5, duration_mult: 0.5 } },
        push_through: { cost: {}, effect: { morale: -5 } },
      },
    },
    coup_attempt: {
      weight: 5,
      severities: ["major", "catastrophic"],
      effects: { morale: -20, military_loss: 0.1 },
      duration: 30,
      responses: {
        purge_military: { cost: {}, effect: { military_loss: 0.3, morale: 20 } },
        negotiate: { cost: { gold: 1000 }, effect: { morale: 5 } },
      },
    },
    tech_breakthrough: {
      weight: 6,
      severities: ["minor"],
      effects: { research_complete: true, tech_production: 1.5 },
      duration: 60,
      responses: {
        publish: { cost: {}, effect: { honor: 5, tension: -2 } },
        classify: { cost: {}, effect: { espionage_defense: 1.2 } },
      },
    },
    refugee_crisis: {
      weight: 10,
      severities: ["minor", "moderate"],
      effects: { population: 1.15, morale: -5, food_consumption: 1.3 },
      duration: 120,
      responses: {
        welcome: { cost: { food: 500 }, effect: { population: 1.25, morale: 3, honor: 5 } },
        close_borders: { cost: {}, effect: { population: 1.0, morale: -3, honor: -5 } },
      },
    },
    resource_depletion: {
      weight: 10,
      severities: ["moderate", "major"],
      effects: { random_resource_halt: 0.5 },
      duration: 180,
      responses: {
        prospect: { cost: { gold: 400, tech: 200 }, effect: { duration_mult: 0.5 } },
        import: { cost: {}, effect: { trade_dependency: 1.5 } },
      },
    },
    diplomatic_incident: {
      weight: 12,
      severities: ["minor", "moderate"],
      effects: { tension: 5, honor: -5 },
      duration: 60,
      responses: {
        apologize: { cost: {}, effect: { tension: -3, honor: 3 } },
        escalate: { cost: {}, effect: { tension: 5, military_power: 1.1 } },
      },
    },
    golden_age: {
      weight: 3, // Very rare
      severities: ["minor"],
      effects: { morale: 20, production_mult: 1.25, research_speed: 1.2 },
      duration: 120,
      responses: {
        celebrate: { cost: { gold: 100 }, effect: { morale: 10, duration_mult: 1.3 } },
        invest: { cost: {}, effect: { production_mult: 1.1 } },
      },
    },
    cyber_attack: {
      weight: 10,
      severities: ["moderate", "major"],
      effects: { tech_loss: 0.1, morale: -5, espionage_defense: 0.8 },
      duration: 45,
      responses: {
        counter_hack: { cost: { tech: 300 }, effect: { espionage_defense: 1.3 } },
        harden: { cost: { gold: 500 }, effect: { duration_mult: 0.3 } },
      },
    },
  },
} as const;

// ============================================
// ADVANCED GOVERNANCE POLICIES (Democracy 4)
// ============================================
export const GOVERNANCE_POLICIES = {
  TYPES: ["military_spending", "education", "infrastructure", "trade_openness", "civil_liberties", "environmental"] as const,
  DEFAULT_LEVEL: 50,
  MIN_LEVEL: 0,
  MAX_LEVEL: 100,
  CHANGE_COST_GOLD: 50,     // Gold cost per policy change
  CHANGE_COOLDOWN_TICKS: 12, // 1 min real time between changes

  EFFECTS: {
    military_spending: {
      // Higher = faster unit recruitment, more unit power
      low: { unit_recruit_speed: 0.7, unit_power: 0.9, gold_drain: 0 },
      high: { unit_recruit_speed: 1.5, unit_power: 1.15, gold_drain: 0.02 },
    },
    education: {
      // Higher = faster research
      low: { research_speed: 0.7, morale: -2 },
      high: { research_speed: 1.4, morale: 2 },
    },
    infrastructure: {
      // Higher = faster building, lower costs
      low: { building_speed: 0.7, building_cost: 1.2 },
      high: { building_speed: 1.5, building_cost: 0.8 },
    },
    trade_openness: {
      // Higher = better trade rates, but vulnerable to disruption
      low: { trade_commission: 1.3, resource_hoarding: 1.2, espionage_defense: 1.1 },
      high: { trade_commission: 0.7, resource_hoarding: 0.8, espionage_defense: 0.9 },
    },
    civil_liberties: {
      // Higher = morale bonus, lower = espionage defense
      low: { morale: -5, espionage_defense: 1.3, population_growth: 0.9 },
      high: { morale: 10, espionage_defense: 0.8, population_growth: 1.1 },
    },
    environmental: {
      // Higher = food bonus, lower = industry bonus
      low: { food_production: 0.8, industrial_production: 1.3, morale: -3 },
      high: { food_production: 1.3, industrial_production: 0.85, morale: 5 },
    },
  },
} as const;

// ============================================
// SEASONS / BATTLE PASS
// ============================================
export const SEASONS = {
  DURATION_DAYS: 28,
  MAX_LEVEL: 50,
  XP_PER_LEVEL: 500,        // 500 XP to level up
  PREMIUM_PRICE_USD: 9.99,

  CHALLENGE_TYPES: ["daily", "weekly", "seasonal"] as const,

  CHALLENGES: {
    win_duel: { xp: 100, description: "Win a duel" },
    win_3_duels: { xp: 300, description: "Win 3 duels" },
    stake_1000: { xp: 50, description: "Stake 1000 coins" },
    complete_research: { xp: 75, description: "Complete a research" },
    execute_strike: { xp: 200, description: "Execute a successful strike" },
    earn_500g_trades: { xp: 100, description: "Earn 500g from trades" },
    build_5_buildings: { xp: 75, description: "Build 5 buildings" },
    recruit_100_units: { xp: 100, description: "Recruit 100 military units" },
    espionage_success: { xp: 150, description: "Complete a successful espionage operation" },
    place_prediction: { xp: 25, description: "Place a prediction on a war" },
    submit_intel: { xp: 50, description: "Submit an intel report" },
    complete_focus: { xp: 200, description: "Complete a national focus" },
  },

  REWARDS: {
    5:  { type: "badge", value: "season_starter" },
    10: { type: "title", value: "Strategist" },
    15: { type: "prediction_bonus", value: 1.1 },     // +10% prediction payouts
    20: { type: "combat_boost", value: 1.05 },         // +5% combat power
    25: { type: "badge", value: "season_veteran" },
    30: { type: "title", value: "Warlord" },
    35: { type: "prediction_bonus", value: 1.15 },
    40: { type: "combat_boost", value: 1.1 },
    45: { type: "badge", value: "season_champion" },
    50: { type: "title", value: "Supreme Commander" },
  },
} as const;

// ============================================
// COVERT OPERATIONS (Sprint 4)
// ============================================
export const COVERT_OPS = {
  TYPES: ["signal_boosters", "combat_stims", "morale_agents", "neural_uplinks", "trade_accelerants"] as const,

  BUFF_DURATION_TICKS: 60,
  WITHDRAWAL_DURATION_TICKS: 30,

  FACILITY_BUILD_COST: { gold: 500, tech: 200 } as Record<string, number>,
  FACILITY_UPGRADE_COST_MULT: 2.0,
  FACILITY_MAX_LEVEL: 5,
  FACILITY_PRODUCTION_RATE: 10,

  PRODUCTION_INPUTS: {
    signal_boosters: { tech: 2, food: 3 },
    combat_stims: { steel: 3, rare_earth: 1 },
    morale_agents: { food: 3, gold: 2 },
    neural_uplinks: { tech: 4, uranium: 1 },
    trade_accelerants: { gold: 3, rare_earth: 2 },
  } as Record<string, Record<string, number>>,

  EFFECTS: {
    signal_boosters: {
      buff: { production_mult: 1.30 },
      withdrawal: { production_mult: 0.80, morale: -5 },
    },
    combat_stims: {
      buff: { military_power: 1.25 },
      withdrawal: { military_power: 0.85, morale: -10 },
    },
    morale_agents: {
      buff: { morale: 20, citizen_retention: 1.10 },
      withdrawal: { morale: -15, defection_chance: 0.05 },
    },
    neural_uplinks: {
      buff: { research_speed: 1.40 },
      withdrawal: { research_speed: 0.75, morale: -3 },
    },
    trade_accelerants: {
      buff: { trade_profit: 1.20, trade_tax: 0.50 },
      withdrawal: { trade_profit: 0.90, trade_tax: 1.02 },
    },
  } as Record<string, { buff: Record<string, number>; withdrawal: Record<string, number> }>,

  COVERT_TRANSFER_BASE_TRANSIT_TICKS: 30,
  COVERT_TRANSFER_INTERCEPT_BASE: 0.15,
  COVERT_TRANSFER_INTERCEPT_SPY_BONUS: 0.25,

  EXPOSURE_PER_FACILITY_PER_TICK: 5,
  EXPOSURE_PER_SUPPLY_LINE: 10,
  EXPOSURE_DECAY_PER_TICK: 2,
  EXPOSURE_BUST_THRESHOLD: 500,
  EXPOSURE_SANITIZE_BUST_THRESHOLD: 700,

  SYNDICATE_CREATE_COST: 1000,
  SYNDICATE_MIN_NATIONS: 2,
  SYNDICATE_MAX_NATIONS: 4,
  SYNDICATE_TRANSFER_BONUS: 0.25,
  SYNDICATE_PRODUCTION_BONUS: 1.10,

  POLICY_TAX_RATES: {
    criminalized: 0,
    decriminalized: 0.10,
    legalized: 0.25,
  } as Record<string, number>,

  COMPROMISED_STATE_THRESHOLD: 0.60,
  COMPROMISED_STATE_TICKS: 500,
  COMPROMISED_STATE_MORALE_PENALTY: -20,
  COMPROMISED_STATE_PRODUCTION_BONUS: 1.50,

  SANITIZE_FEE: 0.20,
  SANITIZE_PROCESS_TICKS: 30,
  MARKET_MAX_ORDERS_PER_NATION: 10,
} as const;

// Backward-compatible alias for existing code referencing DRUGS
export const DRUGS = COVERT_OPS;

// ============================================
// GLOBAL CHAT (Sprint 4)
// ============================================
export const GLOBAL_CHAT = {
  MAX_MESSAGE_LENGTH: 500,
  RATE_LIMIT_PER_MIN: 10,
  HISTORY_LIMIT: 100,

  TRASH_TALK_COST: 5,
  DECLARATION_COST: 50,
  PROPAGANDA_BASE_COST: 200,
  PROPAGANDA_EXTRA_COST_PER_60_TICKS: 50,
  PROPAGANDA_MAX_CONCURRENT: 3,
  PROPAGANDA_DEFAULT_DURATION_TICKS: 60,

  MESSAGE_TYPES: [
    "chat", "trash_talk", "declaration", "threat",
    "system", "propaganda_broadcast", "ops_bust", "nuke_alert",
  ] as const,
} as const;

// ============================================
// PRIZE POOL & BUYBACK (Sprint 4 — WorldPvP)
// ============================================
export const PRIZE_POOL = {
  TRADE_TAX_CONTRIBUTION: 0.005,
  TRASH_TALK_CONTRIBUTION: 1.0,
  PROPAGANDA_CONTRIBUTION: 0.50,
  BUYBACK_INTERVAL_HOURS: 168,
  BUYBACK_PERCENT: 0.80,
  MIN_POOL_FOR_BUYBACK: 1000,
} as const;

// ============================================
// NATION DECORATIONS (Sprint 4 — WorldPvP)
// ============================================
export const DECORATIONS = {
  BANNER: 500,
  MOTTO: 100,
  BORDER_STYLE: 300,
  BIO: 50,
  CUSTOM_IMAGE: 1000,
  ANTHEM_URL: 200,
} as const;

// ============================================
// PRESIDENT (Sprint 4 — WorldPvP Top Holder)
// ============================================
export const PRESIDENT = {
  VETO_COOLDOWN_HOURS: 24,
  EMERGENCY_TREASURY_MAX_PCT: 0.05,
  MOTTO_MAX_LENGTH: 200,
} as const;

// ============================================
// ELIMINATION MODE (Sprint 4 — WorldPvP)
// ============================================
export const ELIMINATION = {
  NUKER_SHARE: 0.50,
  PRIZE_POOL_SHARE: 0.50,
  FREE_DEFECTION: true,
} as const;

// ============================================
// PRESIDENTIAL NUKE VOTING (Sprint 4.5 — WorldPvP)
// ============================================
// In WorldPvP, all presidents collectively vote on which country to nuke.
// Votes are visible during the 12-hour window. After execution, full
// vote breakdown is revealed publicly. Creates drama & accountability.
export const NUKE_VOTE = {
  /** Hours before scheduled nuke that voting opens */
  VOTING_WINDOW_HOURS: 12,
  /** Minimum presidents that must vote (not abstain) for nuke to fire */
  MIN_VOTES_TO_FIRE: 3,
  /** Whether votes are visible to all during voting (WorldPvP: yes) */
  VOTES_PUBLIC_DURING_VOTING: true,
  /** Whether full breakdown is revealed after execution */
  REVEAL_AFTER_EXECUTION: true,
  /** How often auto-sessions are created (hours). 0 = manual only */
  AUTO_SESSION_INTERVAL_HOURS: 48,
  /** Cost in gold for a president to call an emergency nuke vote */
  EMERGENCY_SESSION_COST: 2000,
  /** Can presidents change their vote before window closes? */
  ALLOW_VOTE_CHANGE: true,
  /** Nations with active shields are excluded from target list */
  EXCLUDE_SHIELDED_TARGETS: true,
} as const;

// ============================================
// BUY ANNOUNCEMENTS (Sprint 4.5 — WorldPvP)
// ============================================
// WorldPvP broadcasts all token buys in the global chat.
// Creates FOMO and social proof. Shows buyer username + nation flag.
export const BUY_ANNOUNCEMENTS = {
  /** Minimum trade value (gold) to announce in global chat */
  MIN_TRADE_VALUE_FOR_ANNOUNCEMENT: 100,
  /** Message type used for buy announcements */
  MESSAGE_TYPE: "trade_announcement",
} as const;

// ============================================
// DIPLOMATIC STATEMENTS (Sprint 5 — WorldPvP Geopolitics)
// ============================================
// WorldPvP's magic was the emergent diplomacy: US threatening China,
// UK mediating, Finland wanting to nuke Sweden. This system formalizes it
// with AI agents that can auto-respond to threats.
export const DIPLOMACY = {
  /** Statement types and their global chat visibility */
  STATEMENT_TYPES: ["declaration", "threat", "peace_offer", "alliance_call", "taunt", "ultimatum"] as const,
  /** Cost in gold per statement type */
  COST: {
    declaration: 50,
    threat: 100,
    peace_offer: 25,
    alliance_call: 75,
    taunt: 25,
    ultimatum: 200, // expensive — carries diplomatic weight
  },
  /** Max character length for statements */
  MAX_LENGTH: 500,
  /** Cooldown between statements from same nation (minutes) */
  COOLDOWN_MINUTES: 15,
  /** Tension effects from diplomatic actions */
  TENSION_EFFECTS: {
    threat: 3,
    ultimatum: 5,
    peace_offer: -2,
    taunt: 1,
    declaration: 0,
    alliance_call: 0,
  },
  /** AI agents auto-respond to threats/ultimatums within this window (minutes) */
  AI_RESPONSE_WINDOW_MINUTES: 30,
} as const;

// ============================================
// PRESIDENCY DRAMA (Sprint 5 — WorldPvP Dethroning)
// ============================================
// WorldPvP had incredible drama when someone "aped 9 ETH to dethrone
// the president of North Korea." This tracks transitions and alerts.
export const PRESIDENCY_DRAMA = {
  /** Broadcast dethroning events in global chat */
  BROADCAST_DETHRONING: true,
  /** Track multi-presidency ("Puppet Master" like WorldPvP's "Balancer") */
  TRACK_PUPPET_MASTER: true,
  /** Number of presidencies that triggers Puppet Master alert */
  PUPPET_MASTER_THRESHOLD: 3,
  /** Title awarded for controlling multiple nations */
  PUPPET_MASTER_TITLE: "Shadow Emperor",
} as const;

// ============================================
// MAP PROPAGANDA (Sprint 5 — WorldPvP Territory Marking)
// ============================================
// WorldPvP: "Mongolia placed propaganda images over several large countries"
// Nations can mark other nations' territories with images/text/flags.
export const MAP_PROPAGANDA = {
  COSTS: {
    image_url: 500,
    text_overlay: 100,
    flag_plant: 200,
    taunt: 50,
  },
  /** How long propaganda stays active (hours). 0 = permanent until overwritten */
  DURATION_HOURS: 48,
  /** Can the target nation remove propaganda for free? */
  FREE_REMOVAL_BY_TARGET: true,
  /** Overwriting costs 50% more than original placement */
  OVERWRITE_COST_MULTIPLIER: 1.5,
  /** Max active propaganda per nation (as placer) */
  MAX_ACTIVE_PER_NATION: 10,
} as const;

// ============================================
// LOYALTY VAULT (Sprint 6 — WorldPvP Secondary Prize Remix)
// ============================================
// WorldPvP had a "secondary prize" that punished insta-dumpers.
// Our spin: a delayed bonus vault paid to loyal citizens who keep their stake.
export const LOYALTY_VAULT = {
  /** Delay before payout (hours) */
  PAYOUT_DELAY_HOURS: 24,
  /** Portion of platform fee routed into the vault (0..1) */
  PLATFORM_FEE_SHARE: 0.50,
  /** Must keep at least this % of snapshot stake to qualify */
  RETENTION_THRESHOLD_PCT: 0.80,
  /** Ignore snapshots below this stake (prevents dust spam) */
  MIN_SNAPSHOT_STAKE: 1,
  /** Max recipients processed per vault payout (safety) */
  MAX_RECIPIENTS: 2000,
} as const;

// ============================================
// HOLDINGS IDENTITY (Sprint 6 — "Who Actually Has The Bag")
// ============================================
// WorldPvP made chat hilarious by showing flags for actual token holders.
// Our spin: show both citizenship flag and biggest-bag flag (cached).
export const HOLDINGS_IDENTITY = {
  /** Cache TTL for bag identity lookups (seconds) */
  CACHE_TTL_SECONDS: 600,
  /** Max time we will wait to compute identity during a chat send (ms) */
  LOOKUP_TIMEOUT_MS: 350,
  /** If the biggest bag is below this, don't show a bag flag */
  MIN_BAG_BALANCE: 1n,
} as const;

// ============================================
// SCIENTISTS (Intelligence Enhancement)
// ============================================
export const SCIENTISTS = {
  /** Scientists per tech_lab building */
  BASE_PER_LAB: 2,
  /** Research speed bonus per scientist (5% each) */
  RESEARCH_BONUS: 0.05,
  /** Max scientists per nation */
  MAX_PER_NATION: 20,
  /** Cost to recruit additional scientists */
  RECRUITMENT_COST: { gold: 100, tech: 50 },
} as const;

// ============================================
// WIRETAP (Intelligence System)
// ============================================
export const WIRETAP = {
  /** Duration in ticks for each access level */
  DURATION_TICKS: {
    basic: 60,       // 5 minutes
    enhanced: 120,   // 10 minutes
    deep: 180,       // 15 minutes
  } as Record<string, number>,
  /** Max active wiretaps per nation */
  MAX_PER_NATION: 2,
  /** Resource cost per access level */
  COST: {
    basic:    { gold: 300, tech: 100 },
    enhanced: { gold: 600, tech: 200 },
    deep:     { gold: 1000, tech: 400 },
  } as Record<string, Record<string, number>>,
  /** Focus tree requirements for access levels */
  REQUIRED_FOCUS: {
    basic: "intelligence_agency",
    enhanced: "spy_network",
    deep: "puppet_master",
  } as Record<string, string>,
  /** Chance of detection per tick */
  DETECTION_CHANCE: 0.005,
} as const;
